import * as Popover from '@radix-ui/react-popover';
import { Braces, ChevronDown, Download, FileCode2, FileText } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import type { LookupParams } from '@/lib/bootstrap';
import type { BetlensResult } from '@/types';
import { downloadJson } from '../lib/snapshot';
import { downloadHtml, openPdf } from '../lib/report';

/**
 * A thin action bar above the assembled bet. Save the whole diagnostic as a
 * snapshot in whichever format the ticket system wants:
 *   JSON — the exact machine-readable result (re-loadable)
 *   HTML — a self-contained human-readable report
 *   PDF  — that report, via the browser's print-to-PDF (no PDF library)
 */
export function ResultsToolbar({
  input,
  result,
}: {
  input: LookupParams | null;
  result: BetlensResult;
}) {
  const [open, setOpen] = useState(false);

  const choose = (fn: () => void) => {
    fn();
    setOpen(false);
  };

  const items = [
    {
      icon: Braces,
      label: 'JSON',
      hint: 'Machine-readable · re-loadable',
      run: () => downloadJson(input, result),
    },
    {
      icon: FileCode2,
      label: 'HTML',
      hint: 'Self-contained readable report',
      run: () => downloadHtml(result),
    },
    {
      icon: FileText,
      label: 'PDF',
      hint: 'Print-ready (opens a print dialog)',
      run: () => openPdf(result),
    },
  ];

  return (
    <div className="flex items-center justify-end gap-2 border-b border-hairline bg-surface px-4 py-1.5">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1.5 border px-2.5 py-1',
              'font-condensed text-[10px] font-semibold uppercase tracking-wider',
              open
                ? 'border-accent/40 text-accent'
                : 'border-hairline text-text-dim hover:border-accent/40 hover:text-accent',
            )}
          >
            <Download size={12} />
            Save snapshot
            <ChevronDown size={11} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={4}
            className="z-50 w-[240px] border border-hairline-strong bg-surface-raised py-1 shadow-xl shadow-black/50"
          >
            <div className="px-3 py-1 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
              Save as
            </div>
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.label}
                  type="button"
                  onClick={() => choose(it.run)}
                  className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-surface-sunken"
                >
                  <Icon size={13} className="shrink-0 text-text-dim" />
                  <span className="w-10 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text">
                    {it.label}
                  </span>
                  <span className="text-[10.5px] text-text-faint">{it.hint}</span>
                </button>
              );
            })}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
